'use strict';
import React from 'react';
import classNames from 'classnames';

/**
 * Input field component.
 * InputField does not include any positioning styles, and will expand to 100% width of it's containing element.
 * All input fields include focus and disabled styles. The disabled attribute can be used, or the .is-disabled state class can be added.
 * An enclosing class (InputField) is provided, but is not currently required.
 *
 * Usage:
 *
 * ```Javascript
 * <InputLabel label="name" for="model-name">
 *   <InputValidation>{model.errors.name}</InputValidation>
 *   <InputField name="name" id="model-name" error/>
 * </InputLabel>
 * ```
 *
 * @extends {React.Component}
 * @property {String} disabled Renders the input field as disabled.
 * @property {String} errorMsg Renders the input field with an error validation.
 * @property {Boolean} icon Renders the input field with an icon.
 * @property {Boolean} iconButton Renders the input field with an icon button.
 * @property {String} iconButtonClass Sets the icon button to render.
 * @property {String} iconClass Sets the icon to render.
 */
class InputField extends React.Component {

  renderInputButton (iconButtonClass, disabled) {
    const classes = classNames({
      'InputField-iconButton': true,
      'Icon': true,
      [`Icon--${iconButtonClass}`]: !!iconButtonClass,
      'is-active': !disabled
    });

    return (
      <button className={classes} disabled={disabled}></button>
    );
  }

  renderErrorMsg (errorMsg) {
    return <span className="InputField-error">{errorMsg}</span>
  }

  renderSelectedItemsLabel (selectedItems) {
    return (
      <div className="InputField-selectedItemsLabel">{selectedItems + ' items selected'}</div>
    );
  }

  /**
   * @return {JSX} JSX component markup.
   */
  render () {
    const { disabled, label, errorMsg, iconButtonClass, iconClass,
      selectedItems, isTextArea, ...otherProps } = this.props;

    const classType = isTextArea ? 'textArea' : 'input';
    const inputClasses = classNames({
      [`InputField InputField-${classType}`]: true,
      [`InputField-${classType}--error`]: !!errorMsg,
      'InputField-input--withIcon': iconClass,
      'InputField-input--withIconButton': iconButtonClass
    });

    return (
      <div className="InputField">
        <label className="InputField-label">
          { label }
          {!!iconClass ? <i className="Icon Icon--%{iconClass} InputField-icon"></i> : null}
          { isTextArea
            ? <textarea {...otherProps} className={inputClasses} disabled={disabled}/>
            : <input {...otherProps} type="text" className={inputClasses} disabled={disabled} />
          }
          { !!iconButtonClass ? this.renderInputButton(iconButtonClass, disabled) : null }
          { !!errorMsg ? this.renderErrorMsg(errorMsg) : null }
          { selectedItems ? this.renderSelectedItemsLabel(selectedItems) : null }
        </label>
      </div>
    );
  }
}

InputField.defaultProps = {
  disabled: false,
  isTextArea: false
};

InputField.propTypes = {
  disabled: React.PropTypes.bool,
  isTextArea: React.PropTypes.bool,
  errorMsg: React.PropTypes.string,
  selectedItems: React.PropTypes.number
};

export default InputField;



// WEBPACK FOOTER //
// ./src/components/clarity-ui/inputs/inputField/inputField.jsx